import React, { useState, useEffect, useCallback } from 'react';
import { toUrl } from '../lib/config';

interface AnketRow {
  id: number;
  cinsiyet: string;
  yasKategorisi: string;
  egitimDurumu: string;
  gorusVeOnerileriniz?: string;
  createdAt: string;
}

const YatanHastaAnketYonetim: React.FC = () => {
  const [anketler, setAnketler] = useState<AnketRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnket, setSelectedAnket] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchAnketler = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = toUrl('/api/yatan-hasta-anket');
      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`Anketler yüklenemedi: ${res.status}`);
      }
      const data = await res.json();
      setAnketler(data.data || []);
    } catch (e: any) {
      console.error('Fetch error:', e);
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnketler();
  }, [fetchAnketler]);

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/yatan-hasta-anket/${deleteId}`), {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Silme işlemi başarısız');
      closeDeleteModal();
      await fetchAnketler();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = async (id: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl(`/api/yatan-hasta-anket/${id}`));
      if (!res.ok) throw new Error('Anket detayı yüklenemedi');
      const data = await res.json();
      setSelectedAnket(data.data);
      setShowDetailModal(true);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredAnketler = anketler.filter(anket =>
    anket.cinsiyet?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    anket.yasKategorisi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    anket.egitimDurumu?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-page" style={{ padding: 20 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Yatan Hasta Anket Yönetimi</h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Toplam: {anketler.length} anket
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Cinsiyet, yaş veya eğitim durumuna göre ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 15px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 14
          }}
        />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 20 }}>Yükleniyor...</div>}

      {!loading && filteredAnketler.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          Henüz anket bulunmuyor.
        </div>
      )}

      {!loading && filteredAnketler.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>ID</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Cinsiyet</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Yaş</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Eğitim</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Tarih</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, fontSize: 13 }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredAnketler.map((anket, idx) => (
                <tr key={anket.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: 12, fontSize: 13 }}>{anket.id}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{anket.cinsiyet || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{anket.yasKategorisi || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{anket.egitimDurumu || '-'}</td>
                  <td style={{ padding: 12, fontSize: 13 }}>{formatDate(anket.createdAt)}</td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => handleShowDetail(anket.id)}
                      style={{
                        padding: '6px 12px',
                        marginRight: 8,
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Detay
                    </button>
                    <button
                      onClick={() => openDeleteModal(anket.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Silme Onay Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeDeleteModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 24,
              maxWidth: 400,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#991b1b' }}>Anketi Sil</h3>
            <p style={{ margin: '0 0 24px 0', color: '#374151' }}>
              Bu anketi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeDeleteModal}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detay Modal */}
      {showDetailModal && selectedAnket && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              maxWidth: 800,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Anket Detayı - ID: {selectedAnket.id}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <DetailItem label="Cinsiyet" value={selectedAnket.cinsiyet} />
              <DetailItem label="Yaş Kategorisi" value={selectedAnket.yasKategorisi} />
              <DetailItem label="Eğitim Durumu" value={selectedAnket.egitimDurumu} />
              <DetailItem label="Tarih" value={formatDate(selectedAnket.createdAt)} />
            </div>

            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ marginBottom: 12 }}>Anket Cevapları</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <DetailItem label="Hasta Odası ve Çevresinde" value={selectedAnket.hastaOdasiVeCevresinde} />
                <DetailItem label="Odadaki Eşyalar" value={selectedAnket.odadakiEsyalar} />
                <DetailItem label="Yemek Sağlayan Personel" value={selectedAnket.yemekSaglayan} />
                <DetailItem label="Yemeklerin Sıcaklığı" value={selectedAnket.yemeklerinSicakligi} />
                <DetailItem label="Doktorum Hastalığım Hakkında Bilgi Verdi" value={selectedAnket.doktorumTarafindanHastaligim} />
                <DetailItem label="Hemşireler Tedavi ve Bakımım İle İlgili Bilgi Verdi" value={selectedAnket.hemsireleTarafindanTedavi} />
                <DetailItem label="Doktorum Bana Karşı İlgiliydi" value={selectedAnket.doktorumBanaKarsi} />
                <DetailItem label="Hemşireler Bana Karşı İlgiliydi" value={selectedAnket.hemsirelernBanaKarsi} />
                <DetailItem label="Temizlik Personeli İlgiliydi" value={selectedAnket.temizlikPersoneli} />
                <DetailItem label="Hastane Çalışanı Kişisel Mahremiyetime Özen Gösterdi" value={selectedAnket.hastaneCalisaninin} />
                <DetailItem label="Hastane Genel Olarak Temizdi" value={selectedAnket.hastanePoliklinikleri} />
                <DetailItem label="Hastalığım İle İlgili Doktoruma Ulaşabildim" value={selectedAnket.hastaligimIleIlgili} />
                <DetailItem label="Hastane Verilen Hizmet İyiydi" value={selectedAnket.hastaneVerilen} />
                <DetailItem label="Hastaneden Ayrılırken Bilgi Verildi" value={selectedAnket.hastanedenAyrilirken} />
              </div>
            </div>

            {selectedAnket.gorusVeOnerileriniz && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ marginBottom: 8 }}>Görüş ve Öneriler</h4>
                <p style={{ margin: 0, padding: 12, backgroundColor: '#f9fafb', borderRadius: 6, fontSize: 14 }}>
                  {selectedAnket.gorusVeOnerileriniz}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value || '-'}</div>
  </div>
);

export default YatanHastaAnketYonetim;
